import { useState } from 'react';
import { useI18n } from '@/context/I18nContext';
import { useToast } from '@/context/ToastContext';
import { Dialog } from '@/components/common/Dialog';
import { ExternalLink, CheckCircle, RotateCcw, Copy, Check } from 'lucide-react';

export interface BugReport {
  id: string;
  environment?: string;
  userId: string;
  reporterName: string;
  reporterEmail: string;
  description: string;
  pageUrl: string;
  userAgent: string;
  screenWidth: number;
  screenHeight: number;
  appLanguage: string;
  status: 'open' | 'resolved';
  createdAt: string;
  resolvedAt: string | null;
}

interface BugDetailDialogProps {
  bug: BugReport | null;
  onClose: () => void;
  onAction: (id: string, action: 'resolve' | 'reopen') => void;
  loading?: boolean;
}

function formatBugAsText(bug: BugReport): string {
  const lines: string[] = [];
  lines.push(`Bug ID: ${bug.id}`);
  lines.push(`Status: ${bug.status}`);
  if (bug.environment) {
    lines.push(`Environment: ${bug.environment}`);
  }
  lines.push(`Reporter: ${bug.reporterName} <${bug.reporterEmail}>`);
  if (bug.userId) {
    lines.push(`User ID: ${bug.userId}`);
  }
  lines.push(`Created At: ${bug.createdAt}`);
  if (bug.resolvedAt) {
    lines.push(`Resolved At: ${bug.resolvedAt}`);
  }
  lines.push(`Page URL: ${bug.pageUrl}`);
  lines.push(`Screen Size: ${bug.screenWidth}x${bug.screenHeight}`);
  lines.push(`Language: ${bug.appLanguage}`);
  lines.push(`User Agent: ${bug.userAgent}`);
  lines.push('');
  lines.push('Description:');
  lines.push(bug.description);
  return lines.join('\n');
}

async function copyToClipboard(text: string): Promise<boolean> {
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // fallback
    }
  }
  try {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    const successful = document.execCommand('copy');
    textArea.remove();
    return successful;
  } catch {
    return false;
  }
}

export function BugDetailDialog({ bug, onClose, onAction, loading }: BugDetailDialogProps) {
  const { t } = useI18n();
  const { showToast } = useToast();
  const [copied, setCopied] = useState(false);

  if (!bug) return null;

  const isOpen = bug.status === 'open';

  const handleCopy = async () => {
    const text = formatBugAsText(bug);
    const success = await copyToClipboard(text);
    if (success) {
      setCopied(true);
      showToast(t('bugs.copied'));
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Dialog 
      open={!!bug} 
      onClose={onClose} 
      title={t('bugs.detailTitle')}
      footer={
        <div className="flex items-center justify-between gap-2 w-full">
          <button
            type="button"
            onClick={handleCopy}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-text-secondary hover:text-text-primary bg-bg-surface hover:bg-bg-hover border border-border-default hover:border-border-hover rounded-md transition-colors cursor-pointer"
            title={t('bugs.copy')}
          >
            {copied ? (
              <>
                <Check size={16} className="text-green" />
                <span className="text-green">{t('bugs.copied')}</span>
              </>
            ) : (
              <>
                <Copy size={16} />
                <span>{t('bugs.copy')}</span>
              </>
            )}
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-text-secondary hover:text-text-primary bg-bg-surface hover:bg-bg-hover rounded-md transition-colors cursor-pointer"
            >
              {t('common.close')}
            </button>
            {isOpen ? (
              <button
                onClick={() => onAction(bug.id, 'resolve')}
                disabled={loading}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-accent hover:bg-accent-hover rounded-md transition-colors cursor-pointer disabled:opacity-50"
              >
                <CheckCircle size={16} />
                {t('bugs.resolve')}
              </button>
            ) : (
              <button
                onClick={() => onAction(bug.id, 'reopen')}
                disabled={loading}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-amber-500 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/20 rounded-md transition-colors cursor-pointer disabled:opacity-50"
              >
                <RotateCcw size={16} />
                {t('bugs.reopen')}
              </button>
            )}
          </div>
        </div>
      }
    >
      <div className="space-y-6 mt-4">
        {/* Header section */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-lg font-medium text-text-primary">{bug.reporterName}</div>
            <div className="text-sm text-text-secondary mono">{bug.reporterEmail}</div>
          </div>
          <div className="flex items-center gap-2">
            {bug.environment && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-bg-surface text-text-secondary border border-border-default uppercase tracking-wider mono">
                {bug.environment}
              </span>
            )}
            {isOpen ? (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-green-500/10 text-green-500 border border-green-500/20 uppercase tracking-wider">
                {t('bugs.statusOpen')}
              </span>
            ) : (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-bg-surface text-text-muted border border-border-default uppercase tracking-wider">
                {t('bugs.statusResolved')}
              </span>
            )}
          </div>
        </div>

        <div className="space-y-4">
          {/* Description */}
          <div>
            <h4 className="text-sm font-medium text-text-muted mb-1.5">{t('bugs.description')}</h4>
            <div className="p-4 rounded-md bg-bg-surface border border-border-default whitespace-pre-wrap text-sm text-text-primary">
              {bug.description}
            </div>
          </div>

          {/* Page URL */}
          <div>
            <h4 className="text-sm font-medium text-text-muted mb-1.5">{t('bugs.pageUrl')}</h4>
            <a 
              href={bug.pageUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-accent hover:text-accent-hover transition-colors"
            >
              <span className="truncate max-w-[400px]">{bug.pageUrl}</span>
              <ExternalLink size={14} className="shrink-0" />
            </a>
          </div>

          {/* Metadata Grid */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <h4 className="text-xs font-medium text-text-muted mb-1">{t('bugs.createdAt')}</h4>
              <div className="text-sm text-text-primary">
                {new Date(bug.createdAt).toLocaleString()}
              </div>
            </div>
            {bug.resolvedAt && (
              <div>
                <h4 className="text-xs font-medium text-text-muted mb-1">{t('bugs.resolvedAt')}</h4>
                <div className="text-sm text-text-primary">
                  {new Date(bug.resolvedAt).toLocaleString()}
                </div>
              </div>
            )}
            <div>
              <h4 className="text-xs font-medium text-text-muted mb-1">{t('bugs.screenSize')}</h4>
              <div className="text-sm text-text-primary mono">
                {bug.screenWidth} × {bug.screenHeight}
              </div>
            </div>
            <div>
              <h4 className="text-xs font-medium text-text-muted mb-1">{t('bugs.appLanguage')}</h4>
              <div className="text-sm text-text-primary uppercase">
                {bug.appLanguage}
              </div>
            </div>
          </div>

          {/* User Agent */}
          <div>
            <h4 className="text-xs font-medium text-text-muted mb-1">{t('bugs.userAgent')}</h4>
            <div className="text-xs text-text-secondary break-words font-mono bg-bg-surface p-2 rounded border border-border-default">
              {bug.userAgent}
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
